<script setup lang="ts">
import type { Hub } from '@/data/Hubs/types'
import { useHubClient } from '@/data/Hubs/useHubClient'
import { useWorkspaceContext } from '@/modules/Workspaces/useWorkspaceContext'
import DarwinButton from '@/uiKit/DarwinButton.vue'
import IconSprite from '@/uiKit/IconSprite.vue'
import Menu from '@/uiKit/Menu'
import { computed, ref } from 'vue'

const selectedId = defineModel<string | undefined>({ default: undefined })

const { workspaceId } = useWorkspaceContext()
const hubClient = useHubClient({ workspaceId })

const { data: hubs } = hubClient.fetchAll()
const allHubs = computed<Hub[]>(() => Array.from(hubs.value?.values() ?? []))

const searchTerm = ref('')
const filteredHubs = computed<Hub[]>(() => {
  return allHubs.value.filter((hub) => isSearchMatch(hub, searchTerm.value))
})

function isSearchMatch(hub: Hub, search: string) {
  const term = search.toLocaleLowerCase()
  const name = hub.name?.toLocaleLowerCase() || ''
  const description = hub.description?.toLocaleLowerCase() || ''
  return name?.includes(term) || description?.includes(term)
}

const selectedHub = computed<Hub | undefined>(() => {
  return selectedId.value ? allHubs.value.find((h) => h.id === selectedId.value) : undefined
})
</script>

<template>
  <Menu.Root
    v-slot="{ getTriggerProps, menu }"
    close-on-select
  >
    <DarwinButton
      v-bind="{ ...getTriggerProps(), ...$attrs }"
      size="sm"
      variant="transparent"
      rounded
      aria-label="Select a hub"
      :active="menu.open"
      :class="[
        selectedHub &&
          'bg-background-blue-subtle! text-text-selected hover:bg-background-selected-hovered!',
      ]"
    >
      <template #leading-icon>
        <IconSprite
          icon="court"
          :class="[selectedHub ? 'text-text-selected' : 'text-text-subtlest']"
        />
      </template>
      <div
        class="max-w-32 truncate"
        :class="[selectedHub && 'text-text-selected']"
      >
        {{ selectedHub?.name ?? 'No hub' }}
      </div>
      <template
        v-if="!$attrs.disabled"
        #trailing-icon
      >
        <IconSprite icon="chevron-down" />
      </template>
    </DarwinButton>
    <Menu.Content>
      <Menu.ControlledSearch
        v-model="searchTerm"
        autofocus
      />
      <div class="go-scrollbar max-h-[340px] overflow-x-hidden overflow-y-auto">
        <Menu.Item
          label="No hub"
          @select="selectedId = undefined"
        />
        <Menu.Divider v-if="filteredHubs.length > 0" />
        <Menu.Item
          v-for="hub in filteredHubs"
          :key="hub.id"
          :label="hub.name"
          @select="selectedId = hub.id"
        />
      </div>
    </Menu.Content>
  </Menu.Root>
</template>
